<template>
  <Main class="bg-manual">

    <v-container fluid>
      <v-row class="flex-column-reverse flex-md-row">
        <!-- Right Column (Appears on top in small screens) -->
        <v-col cols="12" md="9">
          <v-virtual-scroll
              :height="`${contentHeight}vh`"
              :items="[1]"
          >
            <div class="mt-10" v-if="selectedItem === ''">
              <div class="text-center">
                <h1>USER MANUAL</h1>
                <p class="text-lg-body-1 mt-4">Pick a section to get started.</p>
              </div>
              <div class="text-end">
                <i :class="`fa ${windowWidth > responsiveBreakpointPx ? 'fa-arrow-right' : 'fa-arrow-up'} mr-15`" style="font-size: 100px"/>
              </div>
            </div>
            <component :is="selectedItem.component" />
          </v-virtual-scroll>
        </v-col>

        <!-- Left Column -->
        <v-col cols="12" md="3">
          <v-list class="sidebar py-0" density="compact">
            <v-list-item
                v-for="(item, i) in items"
                :key="i"
                :value="item"
                @click="() => updateContent(item)"
                variant="flat"
                rounded="lg"
                color="blue"
            >
              <v-list-item-title
                  v-if="item.level === 1"
                  v-text="item.title"
              ></v-list-item-title>
              <v-list-item-title
                  v-if="item.level !== 1"
                  v-text="item.title"
                  :class="`level-${item.level}-item`"
              ></v-list-item-title>
            </v-list-item>
          </v-list>
        </v-col>
      </v-row>
    </v-container>
  </Main>
</template>

<script setup>
import {computed, onMounted, onUnmounted, ref} from "vue";
import Intro from "./Intro.vue";
import PpiAnalysis from "./PpiAnalysis.vue";
import NewPsi from "./NewPsi.vue";
import PpiResults from "./PpiResults.vue";
import SiiSpiAnalysis from "./SiiSpiAnalysis.vue";
import NewKpi from "./NewKpi.vue";
import SiiSpiResults from "./SiiSpiResults.vue";
import Main from "@/Layouts/Main.vue";
import AnalyticalTableKpiScores from "@/Pages/UserManual/AnalyticalTableKpiScores.vue";
import TableExtractionToDocx from "@/Pages/UserManual/TableExtractionToDocx.vue";
import ProjectComparison from "@/Pages/UserManual/ProjectComparison.vue";
import KpiComparison from "@/Pages/UserManual/KpiComparison.vue";
import PsiComparison from "@/Pages/UserManual/PsiComparison.vue";
import ProjectManagement from "@/Pages/UserManual/ProjectManagement.vue";
import AddNewProject from "@/Pages/UserManual/AddNewProject.vue";
import ViewProject from "@/Pages/UserManual/ViewProject.vue";
import CloneProject from "@/Pages/UserManual/CloneProject.vue";
import LoadKpisPsisListIntoProject from "@/Pages/UserManual/LoadKpisPsisListIntoProject.vue";
import KpisPsisOverview from "@/Pages/UserManual/KpisPsisOverview.vue";
import DeleteProject from "@/Pages/UserManual/DeleteProject.vue";
import Various from "@/Pages/UserManual/Various.vue";
import UploadingCustomFiles from "@/Pages/UserManual/UploadingCustomFiles.vue";
import UploadKpi from "@/Pages/UserManual/UploadKpi.vue";
import UploadPsi from "@/Pages/UserManual/UploadPsi.vue";
import PossibleSiiSpiErrors from "@/Pages/UserManual/PossibleSiiSpiErrors.vue";
import QuickEdit from "@/Pages/UserManual/QuickEdit.vue";

const items = [
  { title: "Intro",                            level: 1, component: Intro },

  { title: "Project Management",               level: 1, component: ProjectManagement },
  { title: "Add New Project",                  level: 2, component: AddNewProject },
  { title: "View Project",                     level: 2, component: ViewProject },
  { title: "Clone Project",                    level: 2, component: CloneProject },
  { title: "Quick Editing KPIs",               level: 2, component: QuickEdit },
  { title: "Load KPIs/PSIs List Into Project", level: 2, component: LoadKpisPsisListIntoProject },
  { title: "KPIs/PSIs Overview",               level: 2, component: KpisPsisOverview },
  { title: "Delete Project",                   level: 2, component: DeleteProject },

  { title: "Uploading Custom Files",           level: 1, component: UploadingCustomFiles },
  { title: "KPIs",                             level: 2, component: UploadKpi },
  { title: "PSIs",                             level: 2, component: UploadPsi },

  { title: "PPI Analysis",                     level: 1, component: PpiAnalysis },
  { title: "Creation of New PSI",              level: 2, component: NewPsi },
  { title: "PPI Analysis Results",             level: 2, component: PpiResults },

  { title: "SII & SPI Analysis",               level: 1, component: SiiSpiAnalysis },
  { title: "Creation of New KPI",              level: 2, component: NewKpi },
  { title: "Possible Errors",                  level: 2, component: PossibleSiiSpiErrors },
  { title: "SII and SPI Analyses Results",     level: 2, component: SiiSpiResults },
  { title: "Analytical Table KPI Scores",      level: 2, component: AnalyticalTableKpiScores },
  { title: "Table Extraction to .docx File",   level: 3, component: TableExtractionToDocx },

  { title: "Project Comparison",               level: 1, component: ProjectComparison },
  { title: "Various",                          level: 1, component: Various },

];

const selectedItem = ref("");

function updateContent(e) {
  selectedItem.value = e;
}

const responsiveBreakpointPx = 960
const windowHeight = ref(window.innerHeight)
const windowWidth = ref(window.innerWidth)

/**
 * Dynamically compute the height in vh for the content
 * sections. This is useful for smaller screens when the
 * available height for scrolling fluctuates.
 * @type {ComputedRef<number|*>}
 */
const contentHeight = computed(() => {
  if (windowWidth.value > responsiveBreakpointPx) {
    return 88
  }
  const menuHeight = 282;
  return (1 - (menuHeight / windowHeight.value) - 0.12) * 100
})

function onWindowSizeChange() {
  windowHeight.value = window.innerHeight
  windowWidth.value = window.innerWidth
}

onMounted(() => {
  window.addEventListener("resize", onWindowSizeChange);
})

onUnmounted(() => {
  window.removeEventListener("resize", onWindowSizeChange);
})

</script>

<style scoped>
.bg-manual {
  background: linear-gradient(to bottom, #f4f9ff, #e6eeff 50%, #e6eeff) !important;
}

.sidebar {
  border: solid 1px #0e47a0a0 !important;
  border-radius: 20px !important;
}

.level-2-item {
  padding-left: 20px !important;
}

.level-3-item {
  padding-left: 60px !important;
}

html,
body {
  height: 100%;
  margin: 0;
  overflow: hidden;
}
</style>
